import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  Button,
  alpha,
  useTheme,
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import type { QuizQuestion as QuizQuestionType } from './quizTypes';

interface QuizQuestionProps {
  question: QuizQuestionType;
  /** Currently selected value (string for single-select, string[] for multi). */
  value: string | string[] | undefined;
  /** Fires when the user picks a choice. */
  onSelect: (choiceId: string) => void;
  /** For multi-select: fires when user clicks "See My Results". */
  onContinue?: () => void;
}

const QuizQuestion: React.FC<QuizQuestionProps> = ({
  question,
  value,
  onSelect,
  onContinue,
}) => {
  const theme = useTheme();
  const isMulti = question.multiSelect === true;
  const selectedSet = new Set(
    isMulti ? (value as string[] | undefined) ?? [] : value ? [value as string] : [],
  );

  return (
    <Box>
      {/* Title */}
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, color: 'text.primary', mb: question.subtitle ? 0.5 : 2 }}
      >
        {question.title}
      </Typography>

      {question.subtitle && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {question.subtitle}
        </Typography>
      )}

      {/* Choices */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {question.choices.map((choice) => {
          const isSelected = selectedSet.has(choice.id);

          return (
            <Paper
              key={choice.id}
              elevation={0}
              onClick={() => onSelect(choice.id)}
              sx={{
                p: 2,
                cursor: 'pointer',
                border: 2,
                borderColor: isSelected ? 'text.primary' : 'divider',
                borderRadius: 2,
                bgcolor: isSelected
                  ? alpha(theme.palette.text.primary, 0.04)
                  : 'background.paper',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'text.primary',
                  transform: 'translateX(4px)',
                },
              }}
            >
              {/* Multi-select checkbox */}
              {isMulti && (
                <Checkbox
                  checked={isSelected}
                  sx={{
                    p: 0,
                    color: 'text.secondary',
                    '&.Mui-checked': { color: 'text.primary' },
                  }}
                  tabIndex={-1}
                  disableRipple
                />
              )}

              {/* Label + description */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, color: 'text.primary' }}
                >
                  {choice.label}
                </Typography>
                {choice.description && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                    {choice.description}
                  </Typography>
                )}
              </Box>

              {/* Single-select arrow */}
              {!isMulti && (
                <ArrowForward
                  sx={{
                    fontSize: 20,
                    color: 'text.secondary',
                    opacity: isSelected ? 1 : 0,
                    transition: 'opacity 0.2s',
                    '.MuiPaper-root:hover &': { opacity: 1 },
                  }}
                />
              )}
            </Paper>
          );
        })}
      </Box>

      {/* Multi-select continue button */}
      {isMulti && (
        <Button
          fullWidth
          variant="contained"
          disabled={selectedSet.size === 0}
          onClick={onContinue}
          sx={{
            mt: 3,
            py: 1.25,
            bgcolor: 'text.primary',
            color: 'background.paper',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { opacity: 0.85 },
            '&.Mui-disabled': {
              bgcolor: alpha(theme.palette.text.primary, 0.12),
              color: alpha(theme.palette.text.primary, 0.38),
            },
          }}
        >
          See My Results
        </Button>
      )}
    </Box>
  );
};

export default QuizQuestion;
